import defaultCenterImage from '../assets/svg/center_image_example.svg';
import React from 'react';
import styled from 'styled-components';

interface QuestionProgressDisplayProps {
    /**
     * Total Amount of questions
     */
    amountOfQuestions?: number;

    /**
     * Question progress (out of total amount of questions)
     */
    progress?: number;

    /**
     * Total size of the element (in pixels)
     */
    size?: number;

    /**
     * Padding for the sides (in pixels)
     */
    sideMargin?: number;

    /**
     * Width of the circle tick (in pixels)
     */
    tickWidth?: number;

    /**
     * Circle radius (in pixels)
     */
    radius?: number;

    /**
     * Tick Spacing in % of PI
     * @default 0.02
     */

    tickSpacing?: number;

    /**
     * Center Image url
     */
    centerImage?: string;

    /**
     * Color of the tick when the question is done or in progress
     */
    doneProgressColor?: string;

    /**
     * Color of the tick when the question is to do
     */
    toDoColor?: string;

    className?: string;
    centerText?: string;
}

const QuestionProgressDisplayWrapper = styled.svg``;

const QuestionProgressMiddleImage = styled.image``;

const QuestionProgressProgressWrapper = styled.g``;

const QuestionProgressTick = styled.path`
    fill: none;
`;

const getXCoordinateForPoint = (
    circleRadius: number,
    tickSpacing: number,
    step: number,
    totalAmountOfArcs: number,
): number =>
    circleRadius *
    Math.cos(
        (step * 2 * Math.PI) / totalAmountOfArcs +
            tickSpacing * Math.PI -
            Math.PI / 2,
    );

const getYCoordinateForPoint = (
    circleRadius: number,
    tickSpacing: number,
    step: number,
    totalAmountOfArcs: number,
): number =>
    circleRadius *
    Math.sin(
        (step * 2 * Math.PI) / totalAmountOfArcs +
            tickSpacing * Math.PI -
            Math.PI / 2,
    );

const getPathForCircleArc = (
    radius: number,
    step: number,
    tickSpacing: number,
    totalAmountOfArcs: number,
): string => {
    const x0 = getXCoordinateForPoint(
        radius,
        tickSpacing,
        step,
        totalAmountOfArcs,
    );
    const y0 = getYCoordinateForPoint(
        radius,
        tickSpacing,
        step,
        totalAmountOfArcs,
    );

    /**
     * No tickSpacing for destination (would cancel tickSpacing)
     */
    const x1 = getXCoordinateForPoint(radius, 0, step + 1, totalAmountOfArcs);
    const y1 = getYCoordinateForPoint(radius, 0, step + 1, totalAmountOfArcs);

    const xAxisRotation = 0;
    const largeArcFlag = 0;
    const sweepFlag = 1;

    /**
     * M: Move
     * A : Eliptical arc
     * See: https://www.w3.org/TR/SVG/paths.html#PathDataEllipticalArcCommands
     * */
    return `M ${x0} ${y0} A ${radius} ${radius} ${xAxisRotation} ${largeArcFlag} ${sweepFlag} ${x1} ${y1}`;
};

const QuestionProgressDisplay: React.FC<QuestionProgressDisplayProps> = ({
    amountOfQuestions = 2,
    progress = 1,
    size = 500,
    tickWidth = 5,
    sideMargin = 10,
    tickSpacing = 0.01,
    centerImage = defaultCenterImage,
    doneProgressColor = '#CDCDCD',
    toDoColor = 'black',
    className,
    centerText,
}): React.ReactElement => {
    const radius = size / 2 - sideMargin;

    /**
     * For the image to be in the middle, it has to be smaller than the whole svg
     */
    const imageSize = size / 1.5 - sideMargin;

    // --- MOVE THIS LOGIC ABOVE RETURN ---
    // Example static text, split into lines of max 18 chars for demo
    const maxCharsPerLine = 18;
    const words = centerText?.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words?.forEach((word) => {
        if ((currentLine + ' ' + word).trim().length > maxCharsPerLine) {
            lines.push(currentLine.trim());
            currentLine = word;
        } else {
            currentLine += ' ' + word;
        }
    });
    if (currentLine) lines.push(currentLine.trim());

    // Calculate font size and line height dynamically
    const maxFontSize = imageSize * 1.2; // Increased from 0.95 to 1.2 (allows larger than circle)
    const minFontSize = 14;
    const maxTextWidth = imageSize * 0.95; // Increased from 0.85 to 0.95

    // IMPORTANT: Change the character width estimation from 0.55 to 0.5 or less
    // This allows more characters to fit at the same font size
    let fontSize = maxFontSize;

    // Set initial fits status - check if the text fits at max size
    let fits = lines.every(
        (line) => line.length * fontSize * 0.5 <= maxTextWidth, // Changed from 0.55 to 0.5
    );

    // Only enter loop if text doesn't fit at max size
    while (!fits && fontSize > minFontSize) {
        fontSize -= 1;
        fits = lines.every(
            (line) => line.length * fontSize * 0.5 <= maxTextWidth, // Changed from 0.55 to 0.5
        );
    }

    const lineHeight = fontSize * 1.2;
    const startY = 50 - ((lines.length - 1) / 2) * ((lineHeight * 100) / size);

    // --- END LOGIC TO MOVE ---

    return (
        <QuestionProgressDisplayWrapper
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className={className}
        >
            {centerText ? (
                // Render text if centerText is provided
                <text
                    x="50%"
                    y={`${startY}%`}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fontFamily="inherit"
                    fill="#fff"
                >
                    {lines.map((line, idx) => (
                        <tspan
                            key={idx}
                            x="50%"
                            dy={idx === 0 ? 0 : `${(lineHeight * 100) / size}%`}
                            dominantBaseline="middle"
                        >
                            {line}
                        </tspan>
                    ))}
                </text>
            ) : (
                // Render image if no centerText
                <QuestionProgressMiddleImage
                    href={centerImage}
                    width={imageSize}
                    height={imageSize}
                    x={'50%'}
                    y={'50%'}
                    transform={`translate(${-(imageSize / 2)}, ${-(
                        imageSize / 2
                    )})`}
                />
            )}

            <QuestionProgressProgressWrapper
                transform={`translate(${size / 2}, ${size / 2})`}
                strokeWidth={tickWidth}
                width={size}
                height={size}
            >
                {/** Display as many ticks as there are questions */}
                {[...Array(amountOfQuestions)].map(
                    (e, i): React.ReactElement => (
                        <QuestionProgressTick
                            key={`question-indicator-${i}`}
                            stroke={
                                i < progress ? doneProgressColor : toDoColor
                            }
                            d={getPathForCircleArc(
                                radius,
                                i,
                                tickSpacing,
                                amountOfQuestions,
                            )}
                        />
                    ),
                )}
            </QuestionProgressProgressWrapper>
        </QuestionProgressDisplayWrapper>
    );
};

export default QuestionProgressDisplay;